import { evaluate, parse, FunctionNode } from 'mathjs';

/**
 * Result of expression evaluation
 */
export interface EvaluationResult {
  success: boolean;
  value?: number;
  error?: string;
  originalExpression: string;
}

/**
 * Configuration options for the evaluator
 */
export interface EvaluatorOptions {
  /** Maximum range for x values to prevent infinite loops */
  maxRange?: number;
  /** Precision for floating point comparisons */
  precision?: number;
  /** Whether to allow complex numbers */
  allowComplex?: boolean;
  /** Custom variable substitutions */
  variables?: Record<string, number>;
}

/**
 * Advanced Mathematical Expression Evaluator
 *
 * Provides robust evaluation of mathematical expressions including:
 * - Powers and exponents (x^n, n^x, x^(1/2))
 * - Trigonometric functions (sin, cos, tan, sec, csc, cot, arcsin, arccos, arctan)
 * - Logarithmic functions (log, ln, log10, log2)
 * - Exponential functions (exp, e^x, arbitrary bases)
 * - Complex expressions and nested functions
 * - Mathematical constants (pi, e)
 *
 * Backward compatible with existing linear equation formats.
 */
export class AdvancedMathEvaluator {
  private options: Required<EvaluatorOptions>;
  private originalExpression: string;
  private parsedExpression: any;
  private isLinearEquation: boolean = false;
  private linearParams: { slope: number; intercept: number } | null = null;

  constructor(expression: string, options: EvaluatorOptions = {}) {
    this.options = {
      maxRange: 1000,
      precision: 1e-10,
      allowComplex: false,
      variables: {},
      ...options
    };

    this.originalExpression = expression;
    this.parseExpression(expression);
  }

  /**
   * Parse and prepare the mathematical expression
   */
  private parseExpression(expression: string): void {
    try {
      // First, try to detect if this is a linear equation (backward compatibility)
      const linearResult = this.tryParseLinearEquation(expression);
      if (linearResult) {
        this.isLinearEquation = true;
        this.linearParams = linearResult;
        return;
      }

      // Preprocess the expression for common mathematical notation
      const normalizedExpression = this.normalizeExpression(expression);

      // Parse using mathjs
      this.parsedExpression = parse(normalizedExpression);

      // Validate the expression contains valid mathematical operations
      this.validateExpression(this.parsedExpression);

    } catch (error) {
      throw new Error(`Failed to parse expression "${expression}": ${error}`);
    }
  }

  /**
   * Normalize mathematical expression for better parsing
   */
  private normalizeExpression(expression: string): string {
    let normalized = expression.toLowerCase().trim();

    // Remove equation prefix if present (y = ...)
    normalized = normalized.replace(/^y\s*=\s*/, '');

    // Handle common mathematical notation
    normalized = normalized
      // Convert LaTeX-style functions
      .replace(/\\sin\(/g, 'sin(')
      .replace(/\\cos\(/g, 'cos(')
      .replace(/\\tan\(/g, 'tan(')
      .replace(/\\log\(/g, 'log(')
      .replace(/\\ln\(/g, 'log(')
      .replace(/\\exp\(/g, 'exp(')
      .replace(/\\sqrt\{([^}]+)\}/g, 'sqrt($1)')

      // Handle implied multiplication
      .replace(/(\d+)([a-zA-Z])/g, '$1*$2')
      .replace(/([a-zA-Z])(\d+)/g, '$1*$2')
      .replace(/\)(\d+)/g, ')*$1')
      .replace(/(\d+)\(/g, '$1*(')

      // Handle common constants
      .replace(/\bpi\b/g, 'pi')
      .replace(/\be\b(?!\s*\^)/g, 'e')

      // Handle power notation
      .replace(/\^/g, '^')
      .replace(/²/g, '^2')
      .replace(/³/g, '^3')

      // Handle fractions
      .replace(/(\d+)\/(\d+)/g, '($1/$2)')

      // Ensure proper spacing around operators
      .replace(/([+\-*/^])/g, ' $1 ')
      .replace(/\s+/g, ' ')
      .trim();

    return normalized;
  }

  /**
   * Try to parse as a linear equation for backward compatibility
   */
  private tryParseLinearEquation(expression: string): { slope: number; intercept: number } | null {
    const cleanExpr = expression.replace(/\s/g, '').toLowerCase();

    // Remove y= prefix if present
    const withoutY = cleanExpr.replace(/^y=/, '');

    // Match various linear equation patterns (with and without explicit multiplication)
    const patterns = [
      // y = mx + c or y = mx - c (with explicit multiplication: 3*x+1)
      /^(-?\d*\.?\d*)\*?x([+-]\d+\.?\d*)?$/,
      // y = c + mx or y = c - mx (with explicit multiplication: 1+3*x)
      /^(-?\d+\.?\d*)([+-])(\d*\.?\d*)\*?x$/,
      // y = constant
      /^(-?\d+\.?\d*)$/
    ];

    for (const pattern of patterns) {
      const match = withoutY.match(pattern);
      if (match) {
        if (pattern.source.includes('x')) {
          // Linear equation with x
          if (match[3] !== undefined) {
            // Pattern: c +/- mx
            const intercept = parseFloat(match[1]);
            const sign = match[2] === '+' ? 1 : -1;
            const slope = parseFloat(match[3] || '1') * sign;
            return { slope, intercept };
          } else {
            // Pattern: mx +/- c
            const slope = parseFloat(match[1] || '1');
            const intercept = parseFloat(match[2] || '0');
            return { slope, intercept };
          }
        } else {
          // Constant equation
          const intercept = parseFloat(match[1]);
          return { slope: 0, intercept };
        }
      }
    }

    return null;
  }

  /**
   * Validate that the parsed expression is safe and valid
   */
  private validateExpression(node: any): void {
    // Check for unsupported or dangerous operations
    const unsupportedFunctions = ['eval', 'import', 'createUnit'];

    const checkNode = (n: any): void => {
      if (n instanceof FunctionNode && unsupportedFunctions.includes(n.fn.name)) {
        throw new Error(`Unsupported function: ${n.fn.name}`);
      }

      if (n.args) {
        n.args.forEach(checkNode);
      }
      if (n.content) {
        checkNode(n.content);
      }
    };

    checkNode(node);
  }

  /**
   * Evaluate the expression for a given x value
   */
  public evaluate(x: number): EvaluationResult {
    try {
      // Validate input
      if (!isFinite(x) || Math.abs(x) > this.options.maxRange) {
        return {
          success: false,
          error: `Input value ${x} is outside valid range [-${this.options.maxRange}, ${this.options.maxRange}]`,
          originalExpression: this.originalExpression
        };
      }

      let result: number;

      if (this.isLinearEquation && this.linearParams) {
        // Use optimized linear evaluation
        result = this.linearParams.slope * x + this.linearParams.intercept;
      } else {
        // Use mathjs evaluation
        const scope = {
          x,
          ...this.options.variables
        };

        const evaluated = evaluate(this.parsedExpression, scope);

        // Handle complex numbers if not allowed
        if (typeof evaluated === 'object' && evaluated.im !== undefined) {
          if (!this.options.allowComplex) {
            return {
              success: false,
              error: 'Complex numbers are not allowed in this context',
              originalExpression: this.originalExpression
            };
          }
          result = evaluated.re; // Use real part
        } else {
          result = Number(evaluated);
        }
      }

      // Validate result
      if (!isFinite(result)) {
        return {
          success: false,
          error: 'Evaluation resulted in invalid number (NaN or Infinity)',
          originalExpression: this.originalExpression
        };
      }

      return {
        success: true,
        value: result,
        originalExpression: this.originalExpression
      };

    } catch (error) {
      return {
        success: false,
        error: `Evaluation error: ${error}`,
        originalExpression: this.originalExpression
      };
    }
  }

  /**
   * Create a function that can be used for plotting
   */
  public createFunction(): (x: number) => number {
    return (x: number) => {
      const result = this.evaluate(x);
      if (result.success && result.value !== undefined) {
        return result.value;
      }
      // Return NaN for invalid results to avoid plotting issues
      return NaN;
    };
  }

  /**
   * Get information about the expression
   */
  public getInfo(): {
    originalExpression: string;
    isLinearEquation: boolean;
    hasVariables: boolean;
    complexity: 'simple' | 'moderate' | 'complex';
  } {
    let complexity: 'simple' | 'moderate' | 'complex' = 'simple';

    if (this.isLinearEquation) {
      complexity = 'simple';
    } else if (this.parsedExpression) {
      const exprString = this.parsedExpression.toString();
      if (exprString.includes('sin') || exprString.includes('cos') || exprString.includes('tan') ||
          exprString.includes('log') || exprString.includes('exp') || exprString.includes('^')) {
        complexity = 'complex';
      } else if (exprString.includes('*') || exprString.includes('/')) {
        complexity = 'moderate';
      }
    }

    return {
      originalExpression: this.originalExpression,
      isLinearEquation: this.isLinearEquation,
      hasVariables: Object.keys(this.options.variables).length > 0,
      complexity
    };
  }
}

/**
 * Convenience function to create an evaluator function from an expression string
 * This maintains compatibility with existing createEvaluator usage
 */
export function createAdvancedEvaluator(
  expression: string,
  options?: EvaluatorOptions
): (x: number) => number {
  try {
    const evaluator = new AdvancedMathEvaluator(expression, options);
    return evaluator.createFunction();
  } catch (error) {
    console.warn(`Failed to create evaluator for "${expression}": ${error}`);
    // Fallback: return a function that always returns 0
    return () => 0;
  }
}

/**
 * Test function to verify evaluator capabilities
 */
export function testAdvancedEvaluator(): void {
  const testCases = [
    // Linear equations (backward compatibility)
    'y = 2x + 1',
    'y = -x + 3',
    'y = 0.5x',
    'y = 5',

    // Powers and exponents
    'x^2',
    'x^3',
    'x^(1/2)',
    '2^x',
    'x^(-1)',

    // Trigonometric functions
    'sin(x)',
    'cos(x)',
    'tan(x)',
    '2*sin(3*x + 1)',
    'cos(x^2)',

    // Logarithmic functions
    'log(x)',
    'log10(x)',
    'log(x, 2)',
    '2*log(3*x + 1)',

    // Exponential functions
    'exp(x)',
    'e^x',
    '2*exp(x/2)',

    // Complex expressions
    'sin(x^2) + cos(2*x)',
    'log(exp(x))',
    'x^2 * sin(x) + exp(-x/2)'
  ];

  console.log('Testing Advanced Math Evaluator:');

  testCases.forEach(expr => {
    try {
      const evaluator = new AdvancedMathEvaluator(expr);
      const func = evaluator.createFunction();
      const testValue = func(1);
      const info = evaluator.getInfo();

      console.log(`✓ ${expr} => f(1) = ${testValue} [${info.complexity}]`);
    } catch (error) {
      console.log(`✗ ${expr} => Error: ${error}`);
    }
  });
}